import { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import type { SetTrend } from '../account';

/**
 * How a question set has moved, run by run.
 *
 * The stats table answered *which set is doing well* with a single arrow. It
 * could not say "this set named you four times a week ago and once today", which
 * is the entire reason to measure the same questions repeatedly.
 *
 * This IS a time series, unlike the bar chart that used to sit under that table
 * and rank sets against each other — different sets ask different questions, so
 * ranking them says more about the questions than about anything that changed.
 * A set against its own past is the one comparison that holds.
 */

/** Validated for CVD separation and against the dark surface; red and green are
 *  reserved for status and never used as a series colour. */
const COLOURS = ['#7CC4FF', '#C9A0FF', '#FFB27C', '#7CE0D0', '#E0A0C4', '#A0B4FF'];
const GRID = '#33333D';
const AXIS = '#A9A9B4';

/**
 * Six series, and not a seventh.
 *
 * The palette was checked for colourblind separation as this exact list in this
 * exact order, so a seventh series would have to cycle, and two lines sharing a
 * colour is a chart that lies quietly. Above six the filter above the chart is
 * the answer, and the note says so.
 */
const MAX_SERIES = COLOURS.length;

const engineLabel = (e: string): string => (e === 'gemini' ? 'Gemini' : 'GPT');
/** Redundant encoding, so the engine is never carried by colour alone. */
const engineDash = (e: string): string | undefined => (e === 'openai' ? '5 3' : undefined);

const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

export default function SetTrendChart({ sets, compact = false }: {
  sets: SetTrend[];
  /** Inline under one row: no legend, no toggles, smaller. */
  compact?: boolean;
}): JSX.Element | null {
  const [off, setOff] = useState<Set<string>>(new Set());

  /**
   * One row per scan time, a column per set.
   *
   * Merged on the exact timestamp rather than on the date: two engines run
   * minutes apart are two measurements, and collapsing them onto one day would
   * average away the disagreement between engines, which is real and worth
   * seeing.
   */
  const { rows, names, engines } = useMemo(() => {
    const byTime = new Map<string, Record<string, string | number>>();
    const seen: string[] = [];
    const eng = new Set<string>();
    for (const s of sets) {
      for (const p of s.points) {
        /*
          One line per set AND engine.

          Both engines used to write into one key per set, so a Gemini reading
          and a GPT reading taken minutes apart became consecutive points on the
          same line. The line then zigzagged between two different measurements
          and the shape meant nothing: a set steady at 12% on Gemini and 4% on
          GPT drew as something oscillating wildly. Two measurements of two
          different things are two lines.
        */
        const key = `${s.name} · ${engineLabel(p.engine)}`;
        if (!seen.includes(key)) seen.push(key);
        eng.add(p.engine);
        const row = byTime.get(p.at) ?? { at: p.at, label: shortDate(p.at) };
        row[key] = p.namedPct;
        row[`${key}__meta`] = `${p.mentioned}/${p.asked}`;
        byTime.set(p.at, row);
      }
    }
    // Alphabetical, so a set's two engines sit next to each other and a colour
    // belongs to a series rather than to its position in whatever order the
    // server happened to return.
    seen.sort((a, b) => a.localeCompare(b));
    return {
      rows: [...byTime.values()].sort((a, b) => String(a.at).localeCompare(String(b.at))),
      names: seen,
      engines: eng,
    };
  }, [sets]);

  if (rows.length === 0) return null;

  const drawable = names.slice(0, MAX_SERIES);
  const overflow = names.length - drawable.length;
  const visible = drawable.filter((n) => !off.has(n));
  // A single point per line is the case the dots have to carry, and with two
  // engines that is now per line rather than per chart.
  const single = visible.every((n) => rows.filter((r) => typeof r[n] === 'number').length <= 1);

  /**
   * Scale to what was actually achieved, not to 100%.
   *
   * A brand at 4% against a fixed 0–100 axis draws a flat line along the bottom:
   * every movement it has ever made is inside the first twenty pixels, so the
   * chart says "nothing happens here" about a set that halved. The numbers on
   * the axis and in the tooltip are the real percentages either way — only the
   * headroom changes.
   *
   * Rounded up to a sensible step so the top of the axis is a readable number,
   * and given a floor of 5 so a set sitting at 0–1% does not get a two-point
   * axis where noise looks like a cliff.
   */
  const peak = Math.max(
    ...rows.flatMap((r) => visible.map((n) => (typeof r[n] === 'number' ? (r[n] as number) : 0))),
    0,
  );
  const step = peak <= 10 ? 1 : peak <= 25 ? 5 : 10;
  const top = Math.max(5, Math.min(100, Math.ceil((peak * 1.15) / step) * step));

  return (
    <div>
      {!compact && names.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {drawable.map((n, i) => {
            const on = !off.has(n);
            return (
              <button
                key={n}
                onClick={() => setOff((prev) => {
                  const next = new Set(prev);
                  if (next.has(n)) next.delete(n); else next.add(n);
                  return next;
                })}
                className={`font-mono text-xs rounded-lg px-2.5 py-1 border transition ${
                  on ? 'border-transparent text-warm-dark' : 'border-[#70707D] text-text-secondary'
                }`}
                style={on ? { background: COLOURS[i] } : undefined}
                aria-pressed={on}
              >
                {n}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ height: compact ? 180 : 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 8, left: -14, bottom: 4 }}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" stroke={AXIS} tick={{ fontSize: 11 }} />
            <YAxis stroke={AXIS} tick={{ fontSize: 11 }} unit="%" domain={[0, top]} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: '#26262F', border: `1px solid ${GRID}`, borderRadius: 10, fontSize: 12 }}
              labelStyle={{ color: '#F2F2F4' }}
              // The scan time and the raw counts, not just the percentage: "25%"
              // means something different at 4 questions and at 25.
              // Recharts types these callbacks against its own generic payload,
              // which does not survive a per-series shape. The values are read
              // defensively rather than fought with a cast that would hide a
              // real shape change later.
              labelFormatter={(_l: unknown, p: readonly { payload?: Record<string, unknown> }[]) => {
                const at = p?.[0]?.payload?.at;
                return typeof at === 'string' ? new Date(at).toLocaleString() : '';
              }}
              formatter={(v: unknown, name: unknown, p: { payload?: Record<string, unknown> }) => {
                const meta = p?.payload?.[`${String(name)}__meta`];
                const pct = `${String(v)}%`;
                return [typeof meta === 'string' ? `${pct}  (${meta})` : pct, String(name)];
              }}
            />
            {!compact && names.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {visible.map((n) => (
              <Line
                key={n}
                type="monotone"
                dataKey={n}
                stroke={COLOURS[drawable.indexOf(n)]}
                strokeDasharray={engineDash(n.endsWith(`· ${engineLabel('openai')}`) ? 'openai' : 'gemini')}
                strokeWidth={2}
                connectNulls
                // A single run has no line to draw, so the dot has to carry it.
                dot={{ r: single ? 5 : 4, strokeWidth: 2, stroke: '#1A1A20' }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="font-mono text-xs text-text-secondary/70 mt-1">
        {single
          ? 'One run so far. A second one gives this a direction.'
          : 'Share of the set’s questions where AI named you, per scan. Hover a point for the exact count and time.'}
        {engines.size > 1 && (
          <> Each model gets its own line, because they are separate measurements: solid is Gemini, dashed is GPT.</>
        )}
        {top < 100 && (
          <> The axis tops out at {top}% so the movement is visible. These are still the real percentages.</>
        )}
        {overflow > 0 && (
          <> {overflow} more {overflow === 1 ? 'line is' : 'lines are'} not drawn. Six is as many as this palette can
          keep apart for a colourblind reader, so narrow it with the filter above rather than guessing at two lines
          the same colour.</>
        )}
      </p>
    </div>
  );
}
